import { useRef, useState, useEffect } from 'react';
import { Button, ThemeProvider, createTheme } from '@mui/material';
import { AttachFileAdd } from 'assets/svg/AttachFileAdd';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import MessageModal from 'organisms/MessageModal';

type Props = {
  onChange?: (file: File) => void;
  value?: File | null;
};

const FILE_SIZE_LIMIT = 500 * 1024 * 1024;

export const InputFileButton = (props: Props) => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(props.value || null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { t: tAuditionInfo } = useTranslationWithLang('auditionInfo');

  useEffect(() => {
    setFile(props.value || null);
  }, [props.value]);

  const theme = createTheme({
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            background: 'var(--c-button-background)',
            borderRadius: 20,
            padding: '8px 16px',
            width: '56px',
            height: '36px',
          },
        },
      },
    },
  });

  const fileChosen = () => {
    if (inputFileRef.current) {
      const files = inputFileRef.current.files as FileList;
      if (files != null && files.length > 0) {
        const file = files[0];
        setFile(file);
        if (file.size > FILE_SIZE_LIMIT) {
          setErrorMessage(tAuditionInfo('TU005_023'));
          inputFileRef.current.value = '';
          setFile(null);
          return;
        }
        if (props.onChange) {
          props.onChange(file);
        }
        inputFileRef.current.value = '';
      }
    }
  };

  const onClickChosenButton = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    if (inputFileRef.current) {
      inputFileRef.current!.click();
    }
  };

  return (
    <>
      {errorMessage && (
        <MessageModal
          message={errorMessage}
          closeLabel={tAuditionInfo('TU005_024')}
          onClose={() => setErrorMessage('')}
        />
      )}
      <ThemeProvider theme={theme}>
        <div className="flex items-center w-full">
          <input ref={inputFileRef} type="file" onChange={fileChosen} hidden />
          <Button
            aria-label="file-select-button"
            variant="contained"
            disableElevation
            onClick={onClickChosenButton}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = 'var(--c-button-background-hover)')
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--c-button-background)')}
            title={tAuditionInfo('TU005_021')}
          >
            <AttachFileAdd color="white"></AttachFileAdd>
          </Button>
          <p className="ml-2 truncate max-w-full overflow-hidden text-ellipsis whitespace-normal">
            {file === null ? tAuditionInfo('TU005_020') : file.name}
          </p>
        </div>
      </ThemeProvider>
    </>
  );
};
